import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Dropdown, Form, Offcanvas, Spinner } from "react-bootstrap";

import { isEmail } from "../../../utils/regex";

const titleCase = value => String(value || "").replaceAll(/[-_.]+/g, " ").replace(/\b\w/g, character => character.toUpperCase());

const FERA_PRODUCT_ICON_PATHS = {
    survey: `${process.env.PUBLIC_URL || ""}/branding/fera/icons/survey/fera-survey-icon-dark.svg`,
    sign: `${process.env.PUBLIC_URL || ""}/branding/fera/icons/sign/fera-sign-icon-dark.svg`,
    matters: `${process.env.PUBLIC_URL || ""}/branding/fera/icons/matters/fera-matters-icon-dark.svg`,
    chat: `${process.env.PUBLIC_URL || ""}/branding/fera/icons/chat/fera-chat-icon-dark.svg`,
    render: `${process.env.PUBLIC_URL || ""}/branding/fera/icons/render/fera-render-icon-dark.svg`,
    cal: `${process.env.PUBLIC_URL || ""}/branding/fera/icons/cal/fera-cal-icon-dark.svg`,
    email: `${process.env.PUBLIC_URL || ""}/branding/fera/icons/email/fera-email-icon-dark.svg`,
};

export const formatProductPrice = (price, frequency, planName = "") => {
    const amount = Number(price || 0);
    if (/custom/i.test(planName)) return "Custom pricing";
    if (amount === 0) return "$0 forever";
    const formatted = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: amount % 1 ? 2 : 0 }).format(amount);
    if (frequency === "M") return `${formatted} per month`;
    if (frequency === "A") return `${formatted} per year`;
    if (frequency === "P") return `${formatted} one-time`;
    return formatted;
};

export const formatProductManagersSummary = (management) => {
    const count = Number(management?.summary?.additionalManagers || 0);
    if (management?.access?.isOwner) return count ? `You + ${count} ${count === 1 ? "other" : "others"}` : "Only you";
    return count ? `Owner + ${count} ${count === 1 ? "other" : "others"}` : "Owner only";
};

export const planNameFor = (data, product) => {
    const fallback = String(product?.Product?.name || "").split(".").pop();
    return titleCase(data?.planKey || fallback || "Plan");
};

export const ProductStatusBadge = ({ presentation, removalPending }) => (
    <Badge bg={removalPending ? "warning" : presentation.variant} className="product-status-badge">
        {removalPending ? "De-provisioning" : presentation.label}
    </Badge>
);

export const ProductIdentityHeader = ({
    data,
    loginPending,
    management,
    onLoginProduct,
    onManageAccess,
    onManageSubscription,
    onRemoveProduct,
    presentation,
    product,
    removalPending,
    setupComplete,
}) => {
    const record = product?.Product || {};
    const plan = planNameFor(data, product);
    const price = formatProductPrice(product?.price, product?.frequency, plan);
    const description = record.Code?.description || record.description || (setupComplete
        ? "Configure and administer this managed Feradel product."
        : "Complete the recommended setup while Feradel prepares your workspace.");
    const accessLabel = formatProductManagersSummary(management);
    const canLogin = ["survey", "sign", "chat"].includes(data?.productKey) && data?.canLaunch;
    const hasMoreActions = canLogin || management?.access?.canDeleteProduct;
    const productIcon = FERA_PRODUCT_ICON_PATHS[data?.productKey];
    return (
        <header className="product-settings-hero">
            <div className="product-settings-brand" aria-hidden="true">
                {productIcon ? <img src={productIcon} alt="" /> : String(data?.productName || "F").replace(/^Fera/i, "").slice(0, 1) || "F"}
            </div>
            <div className="product-settings-heading">
                <div className="product-settings-eyebrow">{setupComplete ? "Managed product" : "Guided product setup"}</div>
                <h1 id="product-settings-title">{data?.productName}</h1>
                <p>{description}</p>
                <div className="product-settings-meta-line">
                    <span>{plan} plan</span><span aria-hidden="true">{"\u00b7"}</span><span>{price}</span><span aria-hidden="true">{"\u00b7"}</span>
                    <button type="button" onClick={onManageAccess}>{accessLabel}</button>
                </div>
            </div>
            <div className="product-settings-status">
                <ProductStatusBadge presentation={presentation} removalPending={removalPending} />
                <small>{data?.operation?.currentStep ? titleCase(data.operation.currentStep) : setupComplete ? "Product ready" : "Configuration ready"}</small>
                <div className="product-header-actions">
                    {management?.access?.canManageSubscription ? <Button size="sm" variant="outline-light" onClick={onManageSubscription}>Manage subscription</Button> : null}
                    {hasMoreActions ? (
                        <Dropdown align="end">
                            <Dropdown.Toggle size="sm" variant="link" className="product-more-action" aria-label="More product actions">More</Dropdown.Toggle>
                            <Dropdown.Menu>
                                {canLogin ? (
                                    <Dropdown.Item disabled={loginPending} onClick={onLoginProduct}>
                                        {loginPending ? "Opening product\u2026" : `Log in to ${data.productName}`}
                                    </Dropdown.Item>
                                ) : null}
                                {canLogin && management?.access?.canDeleteProduct ? <Dropdown.Divider /> : null}
                                {management?.access?.canDeleteProduct ? <Dropdown.Item className="text-danger" onClick={onRemoveProduct}>Remove product</Dropdown.Item> : null}
                            </Dropdown.Menu>
                        </Dropdown>
                    ) : null}
                </div>
            </div>
        </header>
    );
};

export const ProductSummaryItem = ({ action, label, value, children }) => (
    <div className="product-summary-item">
        <span>{label}</span>
        {value !== undefined ? <strong>{value}</strong> : null}
        {children}
        {action}
    </div>
);

export const InternalTagEditor = ({ canEdit, onSave, value }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || "");
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    useEffect(() => { if (!editing) setDraft(value || ""); }, [editing, value]);
    const save = async () => {
        setSaving(true);
        setMessage(null);
        const result = await onSave(draft.trim());
        setSaving(false);
        if (result?.success) {
            setEditing(false);
            setMessage({ variant: "success", text: "Internal tag updated." });
        } else {
            setMessage({ variant: "danger", text: result?.error?.message || "The internal tag could not be updated." });
        }
    };
    return (
        <ProductSummaryItem label="Internal tag" value={!editing ? (value || "Not assigned") : undefined}>
            {editing ? (
                <div className="product-tag-editor">
                    <Form.Control
                        size="sm"
                        value={draft}
                        maxLength={100}
                        onChange={event => setDraft(event.target.value)}
                        aria-label="Internal tag"
                        autoFocus
                    />
                    <small>An optional internal label used to identify this product in your organization.</small>
                    <div><Button size="sm" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</Button><Button size="sm" variant="link" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button></div>
                </div>
            ) : canEdit ? <Button size="sm" variant="link" onClick={() => { setMessage(null); setEditing(true); }}>{value ? "Edit" : "Add tag"}</Button> : null}
            {message ? <span className={`product-inline-message text-${message.variant}`} role="status">{message.text}</span> : null}
        </ProductSummaryItem>
    );
};

export const ProductSummaryBand = ({ data, management, onManageAccess, onSaveTag, product }) => {
    const plan = planNameFor(data, product);
    const workspace = data?.licenseeSettings?.values?.organization?.name || management?.owner?.displayName || "Your workspace";
    const managers = formatProductManagersSummary(management);
    return (
        <section className="product-summary-band" aria-label="Product summary">
            <ProductSummaryItem label="Workspace" value={workspace} />
            <ProductSummaryItem label="Plan" value={plan} />
            <ProductSummaryItem label="Price" value={formatProductPrice(product?.price, product?.frequency, plan)} />
            <ProductSummaryItem
                label="Product managers"
                value={managers}
                action={<Button size="sm" variant="link" onClick={onManageAccess}>{management?.access?.canManageAccess ? (management?.summary?.additionalManagers ? "Manage access" : "Add manager") : "View access"}</Button>}
            />
            <InternalTagEditor canEdit={management?.access?.canEditInternalTag} value={product?.key || ""} onSave={onSaveTag} />
            <ProductSummaryItem label="Owner" value={management?.owner?.displayName || "Product owner"} />
        </section>
    );
};

const ManagerRow = ({ canManage, manager, onChangeRole, onRemove }) => (
    <li className="product-manager-row">
        <div className="product-manager-avatar" aria-hidden="true">{manager.displayName?.slice(0, 1) || "?"}</div>
        <div><strong>{manager.displayName}</strong><small>{manager.email}</small></div>
        <div><Badge bg={manager.role === "Owner" ? "primary" : "light"} text={manager.role === "Owner" ? undefined : "dark"}>{manager.role === "ProductManager" ? "Product Manager" : manager.role === "LegacyBilling" ? "Legacy billing access" : manager.role}</Badge><small>{manager.status}</small></div>
        {canManage && manager.role !== "Owner" ? (
            <div className="product-manager-actions">
                {manager.role !== "ProductManager" ? <Button size="sm" variant="outline-primary" onClick={() => onChangeRole(manager)}>Change</Button> : null}
                <Button size="sm" variant="outline-danger" onClick={() => onRemove(manager)}>Remove</Button>
            </div>
        ) : <span aria-label="No actions">{"\u2014"}</span>}
    </li>
);

export const ProductManagementDrawer = ({
    loadError,
    loading,
    management,
    onAdd,
    onChangeRole,
    onHide,
    onInvite,
    onRefresh,
    onRetry,
    onRemove,
    onResend,
    onRevoke,
    onSearch,
    show,
}) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [working, setWorking] = useState(false);
    const [searched, setSearched] = useState(false);
    const [message, setMessage] = useState(null);
    const canManage = Boolean(management?.access?.canManageAccess);
    const rows = useMemo(() => [management?.owner, ...(management?.managers || [])].filter(Boolean), [management]);

    useEffect(() => {
        if (!show) {
            setQuery("");
            setResults([]);
            setSearched(false);
            setMessage(null);
        }
    }, [show]);

    const runSearch = async event => {
        event?.preventDefault();
        if (query.trim().length < 2) return;
        setSearching(true);
        setMessage(null);
        const result = await onSearch(query.trim());
        setSearching(false);
        setSearched(true);
        if (result?.success) setResults(result.rows || []);
        else setMessage({ variant: "danger", text: result?.error?.message || "Feradel users could not be searched." });
    };

    const mutate = async (operation, successText) => {
        setWorking(true);
        setMessage(null);
        const result = await operation();
        setWorking(false);
        if (result?.success) {
            setMessage({ variant: "success", text: successText });
            setQuery("");
            setResults([]);
            setSearched(false);
            await onRefresh();
        } else setMessage({ variant: "danger", text: result?.error?.message || "Management access could not be updated. Please try again. Your existing access settings were not changed." });
    };

    const remove = manager => {
        if (!window.confirm(`Remove ${manager.displayName} as a product manager?\n\nThey will no longer be able to configure or administer this product. Their Feradel account will not be deleted.`)) return;
        mutate(() => onRemove(manager), `${manager.displayName} no longer manages this product.`);
    };
    const revoke = invitation => {
        if (!window.confirm(`Revoke the pending product-management invitation for ${invitation.email}?`)) return;
        mutate(() => onRevoke(invitation), "Invitation revoked.");
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" className="product-management-drawer" aria-labelledby="product-management-title">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title id="product-management-title">Product Management Access</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <p className="product-management-intro">Allow trusted users to configure and administer this product on behalf of the licensee. Sharing management access does not transfer product ownership.</p>
                {loading ? <div className="product-management-loading" role="status"><Spinner size="sm" animation="border" /><span>Loading management access...</span></div> : null}
                {loadError ? <Alert variant="danger">{loadError} <Button size="sm" variant="outline-danger" onClick={onRetry}>Try again</Button></Alert> : null}
                {message ? <Alert variant={message.variant} role="status">{message.text}</Alert> : null}
                <h2>People with access</h2>
                <ul className="product-manager-list">
                    {rows.map(row => <ManagerRow key={row.personUuid || row.email} manager={row} canManage={canManage} onChangeRole={manager => mutate(() => onChangeRole(manager), `${manager.displayName} is now a product manager.`)} onRemove={remove} />)}
                </ul>
                {(management?.invitations || []).length ? (
                    <section className="product-pending-invitations" aria-labelledby="pending-invitations-title">
                        <h2 id="pending-invitations-title">Pending invitations</h2>
                        <ul className="product-manager-list">
                            {management.invitations.map(invitation => (
                                <li className="product-manager-row" key={invitation.uuid}>
                                    <div className="product-manager-avatar" aria-hidden="true">@</div>
                                    <div><strong>{invitation.email}</strong><small>Email sent; the recipient must sign in and accept</small></div>
                                    <div><Badge bg="warning" text="dark">Invitation pending</Badge><small>Product Manager</small></div>
                                    {canManage ? <div className="product-manager-actions"><Button size="sm" variant="outline-primary" onClick={() => mutate(() => onResend(invitation), `Invitation email sent again to ${invitation.email}.`)} disabled={working}>Resend email</Button><Button size="sm" variant="outline-danger" onClick={() => revoke(invitation)} disabled={working}>Revoke</Button></div> : <span aria-label="No actions">{"\u2014"}</span>}
                                </li>
                            ))}
                        </ul>
                    </section>
                ) : null}
                {canManage ? (
                    <section className="product-add-manager" aria-labelledby="add-manager-title">
                        <h2 id="add-manager-title">Add a product manager</h2>
                        <p>Search for an existing Feradel user by name or email. If no account exists, create a pending invitation.</p>
                        <Form onSubmit={runSearch}>
                            <Form.Label htmlFor="product-manager-search">Name or email address</Form.Label>
                            <div className="product-manager-search"><Form.Control id="product-manager-search" value={query} onChange={event => { setQuery(event.target.value); setSearched(false); }} /><Button type="submit" disabled={searching || query.trim().length < 2}>{searching ? <Spinner size="sm" animation="border" aria-label="Searching" /> : "Search"}</Button></div>
                        </Form>
                        {results.length ? <ul className="product-search-results">{results.map(result => <li key={result.personUuid || result.userUuid || result.email}><div><strong>{result.displayName}</strong><small>{result.email}</small></div><Button size="sm" onClick={() => mutate(() => onAdd(result), `${result.displayName} can now manage this product.`)} disabled={working}>Add manager</Button></li>)}</ul> : null}
                        {searched && !results.length ? (
                            <div className="product-manager-empty">
                                <strong>No existing user found</strong>
                                <p>The recipient must accept the invitation or create and sign in to a Feradel account.</p>
                                {isEmail(query.trim()) ? <Button size="sm" variant="outline-primary" onClick={() => mutate(() => onInvite(query.trim()), `Invitation email sent to ${query.trim()}.`)} disabled={working}>Invite {query.trim()}</Button> : <small>Enter a complete email address to create an invitation.</small>}
                            </div>
                        ) : null}
                    </section>
                ) : <Alert variant="light">Only the product owner can add or remove product managers. Your access is read-only here.</Alert>}
            </Offcanvas.Body>
        </Offcanvas>
    );
};
